import React, { useEffect, useState } from 'react';
import ReactStars from 'react-rating-stars-component';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import { submitVerifiedReview } from '../../../Services/trustReviews';

const ReviewClientModal = ({ show, close, demand }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [quality, setQuality] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [punctuality, setPunctuality] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const contract = demand?.contract;

  useEffect(() => {
    if (!show) return;
    setRating(0);
    setComment('');
    setQuality(0);
    setCommunication(0);
    setPunctuality(0);
    setError('');
  }, [show, demand?.demand_id]);

  const closeSafely = () => {
    if (!submitting) close?.();
  };

  const submit = async () => {
    if (submitting) return;
    if (!demand?.demand_id || !contract?.artisan_id) {
      setError('Le projet ou l’artisan est introuvable.');
      return;
    }
    if (rating < 1 || rating > 5) {
      setError('Choisissez une note entre 1 et 5 étoiles.');
      return;
    }
    if (comment.trim().length > 0 && comment.trim().length < 10) {
      setError('Ajoutez au moins 10 caractères, ou laissez le commentaire vide.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      await submitVerifiedReview({
        demandId: demand.demand_id,
        rating,
        comment: comment.trim(),
        dimensions: { quality, communication, punctuality },
      });
      setSubmitting(false);
      close?.();
    } catch (reviewError) {
      setError(reviewError.message || 'Impossible de publier cet avis.');
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={show} close={closeSafely}>
      <Header>
        <div>
          <Eyebrow>Projet terminé</Eyebrow>
          <Title>Évaluer votre artisan</Title>
          <Subtitle>Votre note sera ajoutée au profil public de l’artisan.</Subtitle>
        </div>
        <CloseButton type="button" aria-label="Fermer" onClick={closeSafely}>×</CloseButton>
      </Header>

      <ArtisanCard>
        <Avatar src={contract?.artisan_profile_image || '/images/user.svg'} alt="" />
        <div>
          <strong>{[contract?.artisan_firstname, contract?.artisan_lastname].filter(Boolean).join(' ') || 'Artisan'}</strong>
          <span>{demand?.description || 'Prestation réalisée'}</span>
        </div>
      </ArtisanCard>

      <RatingArea>
        <label>Votre note</label>
        <ReactStars
          count={5}
          value={rating}
          onChange={setRating}
          size={34}
          isHalf={false}
          activeColor="#c0a375"
        />
        <RatingText>{rating ? `${rating}/5` : 'Sélectionnez une note'}</RatingText>
      </RatingArea>

      <DimensionGrid>
        <Dimension><span>Qualité du résultat</span><small>Conformité du travail au devis et soin apporté.</small><ReactStars count={5} value={quality} onChange={setQuality} size={22} activeColor="#c0a375" /></Dimension>
        <Dimension><span>Communication</span><small>Clarté des échanges et suivi du chantier.</small><ReactStars count={5} value={communication} onChange={setCommunication} size={22} activeColor="#c0a375" /></Dimension>
        <Dimension><span>Ponctualité</span><small>Respect de la durée convenue après réception de l’acompte.</small><ReactStars count={5} value={punctuality} onChange={setPunctuality} size={22} activeColor="#c0a375" /></Dimension>
      </DimensionGrid>

      <Field>
        <label htmlFor="review-comment">Votre commentaire <span>(facultatif)</span></label>
        <textarea
          id="review-comment"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Qu’avez-vous apprécié ? Le résultat correspond-il à vos attentes ?"
          maxLength={1000}
        />
        <Counter>{comment.length}/1000</Counter>
      </Field>

      <Notice>Votre avis est vérifié et masqué jusqu’à l’avis de l’autre partie ou la fin du délai de publication.</Notice>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <Actions>
        <SecondaryButton type="button" onClick={closeSafely} disabled={submitting}>Annuler</SecondaryButton>
        <PrimaryButton type="button" onClick={submit} disabled={submitting}>
          {submitting ? 'Publication…' : 'Publier l’avis'}
        </PrimaryButton>
      </Actions>
    </Modal>
  );
};

const Header = styled.header`display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.2rem;`;
const Eyebrow = styled.div`margin-bottom: 0.35rem; color: #a88a5d; font-size: 0.76rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase;`;
const Title = styled.h2`margin: 0; color: #111111; font-size: clamp(1.3rem, 4vw, 1.85rem);`;
const Subtitle = styled.p`margin: 0.4rem 0 0; color: #726b61; line-height: 1.5;`;
const CloseButton = styled.button`width: 2.5rem; height: 2.5rem; flex: 0 0 auto; border: 0; border-radius: 50%; background: #f1ece5; color: #111111; font: inherit; font-size: 1.6rem; cursor: pointer;`;
const ArtisanCard = styled.div`
  display: flex; align-items: center; gap: 0.8rem; padding: 0.9rem; border: 1px solid #e5e1da; border-radius: 0.9rem; background: #faf8f4;
  div { display: grid; gap: 0.15rem; } span { color: #77726b; font-size: 0.82rem; }
`;
const Avatar = styled.img`width: 3.1rem; height: 3.1rem; border-radius: 50%; object-fit: cover; background: #efede8;`;
const RatingArea = styled.div`
  display: grid; justify-items: center; gap: 0.35rem; margin-top: 1rem; padding: 1rem; border-radius: 0.9rem; background: #fff8e9;
  label { color: #4e3b17; font-weight: 900; }
`;
const RatingText = styled.span`color: #796338; font-size: 0.82rem;`;
const DimensionGrid = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.55rem;margin-top:1rem;@media(max-width:38rem){grid-template-columns:1fr;}`;
const Dimension = styled.div`padding:.7rem;border-radius:.8rem;background:#faf8f4;span{display:block;color:#5f5a52;font-size:.72rem;font-weight:800;}small{display:block;min-height:2.2rem;margin:.22rem 0;color:#81796f;font-size:.62rem;line-height:1.35;}`;
const Field = styled.div`
  display: grid; gap: 0.4rem; margin-top: 1rem;
  label { color: #222222; font-weight: 800; } label span { color: #78848d; font-weight: 500; }
  textarea { width: 100%; min-height: 8rem; padding: 0.8rem; border: 1px solid #ddd3c4; border-radius: 0.8rem; resize: vertical; color: #111111; }
`;
const Counter = styled.span`justify-self: end; color: #7a8790; font-size: 0.76rem;`;
const Notice = styled.p`margin: 1rem 0 0; padding: 0.85rem 0.95rem; border-radius: 0.8rem; background: #faf8f4; color: #5c6b76; line-height: 1.5;`;
const ErrorMessage = styled.p`margin: 0.8rem 0 0; color: #a52e27; font-size: 0.88rem;`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 0.7rem; margin-top: 1.25rem; @media (max-width: 520px) { flex-direction: column-reverse; }`;
const BaseButton = styled.button`min-height: 2.8rem; padding: 0.72rem 1.05rem; border-radius: 999px; font: inherit; font-weight: 800; cursor: pointer; &:disabled { opacity: 0.55; cursor: wait; }`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #c0a375; background: #c0a375; color: #111111;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;

export default ReviewClientModal;
